import { useId } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { LOAN_REQUEST_COMMANDS, PERSONAL_STATUS } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    antragsteller: 1 | 2;
};

const FamilyStatus: React.FC<Props> = ({ antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const { control } = useFormContext<SchemaLivingSituationForm>();
    const labelId = useId();

    const options = Object.keys(PERSONAL_STATUS).map((value) => ({
        // @ts-expect-error ts doesn't recognize the dynamic FAMILY_STATUS key as a valid value
        label: t(`LIVING_SITUATION_DATA.FAMILY_STATUS.${value}`) as string,
        value,
    }));

    return (
        <FormField<SchemaLivingSituationForm, string | null>
            control={control}
            name={`antragsteller${antragsteller}.familienstand`}
        >
            {({ error, onChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start" id={labelId}>
                        {t('LIVING_SITUATION_DATA.FAMILY_STATUS.TITLE')}
                    </Label>
                    <DropdownMenu
                        aria-labelledby={labelId}
                        data-testid={`antragsteller${antragsteller}.familienstand`}
                        options={options}
                        value={value ?? undefined}
                        onChange={(newValue) => onChange(newValue as string)}
                        placeholder=""
                        triggerClassName="w-full justify-between"
                        maxHeight={170}
                        className="w-full"
                        disabled={
                            !canExecuteApplicantCommand({
                                applicant: antragsteller,
                                commandName: LOAN_REQUEST_COMMANDS.changeApplicantMaritalStatus,
                                permissions,
                            })
                        }
                        error={error}
                        scrollable
                    />
                </div>
            )}
        </FormField>
    );
};

export default FamilyStatus;
